import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTheme } from '@mui/material';
import DSBox from '../design-system/DSBox';
import DSIconButton from '../design-system/DSIconButton';
import DSSecondaryButton from '../design-system/DSSecondaryButton';
import DSCircularProgress from '../design-system/DSCircularProgress';
import DSTypography from '../design-system/Typography/DSTypography';
import { ReactComponent as LeftIcon } from '../design-system/Icons/LeftIcon.svg';
import { ReactComponent as RightIcon } from '../design-system/Icons/RightIcon.svg';
import useOnboardingImages from '../hooks/useOnboardingImages';
import { getScaledCloudinaryUrl } from '../utils';
import useAnalytics from '../hooks/useAnalytics';

const SLIDE_IMAGE_WIDTH = 1280;
const FADE_DURATION_MS = 200;

interface OnboardingWizardProps {
  onComplete: () => void;
  modal?: boolean;
}

const OnboardingWizard = ({ onComplete, modal }: OnboardingWizardProps) => {
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const indexRef = useRef(0);
  const locked = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const hasTrackedStart = useRef(false);

  const { trackEvent } = useAnalytics();
  const { spacing, palette, zIndex } = useTheme();
  const { data, isLoading } = useOnboardingImages();

  const images = useMemo(() => data?.images ?? [], [data?.images]);

  const scaledImages = useMemo(
    () => images.map((url) => getScaledCloudinaryUrl(url, SLIDE_IMAGE_WIDTH)),
    [images],
  );

  const totalSlides = images.length;

  useEffect(() => {
    if (!isLoading && totalSlides === 0) {
      onCompleteRef.current();
    }
    if (!isLoading && totalSlides > 0 && !modal && !hasTrackedStart.current) {
      hasTrackedStart.current = true;
      trackEvent({ type: 'wizard_started' });
      trackEvent({
        type: 'wizard_slide_viewed',
        context: `slide:1/${totalSlides}`,
      });
    }
  }, [isLoading, totalSlides, modal]);

  useEffect(() => {
    scaledImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [scaledImages]);

  const goTo = useCallback(
    (newIndex: number) => {
      if (locked.current) return;
      if (newIndex < 0 || newIndex >= totalSlides) return;
      locked.current = true;
      indexRef.current = newIndex;
      if (!modal) {
        trackEvent({
          type: 'wizard_slide_viewed',
          context: `slide:${newIndex + 1}/${totalSlides}`,
        });
      }
      setOpacity(0);
      setTimeout(() => {
        setVisibleIndex(newIndex);
        requestAnimationFrame(() => {
          setOpacity(1);
          setTimeout(() => {
            locked.current = false;
          }, FADE_DURATION_MS);
        });
      }, FADE_DURATION_MS);
    },
    [totalSlides],
  );

  const goNext = useCallback(() => {
    if (indexRef.current >= totalSlides - 1) {
      if (!modal) {
        trackEvent({ type: 'wizard_completed' });
      }
      onCompleteRef.current();
    } else {
      goTo(indexRef.current + 1);
    }
  }, [totalSlides, goTo, modal]);

  const goPrev = useCallback(() => {
    goTo(indexRef.current - 1);
  }, [goTo]);

  const handleSkip = useCallback(() => {
    if (!modal) {
      trackEvent({
        type: 'wizard_skipped',
        context: `skippedAtSlide:${indexRef.current + 1}/${totalSlides}`,
      });
    }
    onCompleteRef.current();
  }, [totalSlides, modal]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === 'Escape') handleSkip();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goNext, goPrev, handleSkip]);

  if (isLoading) {
    return (
      <DSBox
        sx={{
          ...(modal
            ? {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '80vw',
                height: '80vh',
                maxWidth: 1000,
              }
            : {
                position: 'fixed',
                inset: 0,
                zIndex: zIndex.modal,
              }),
          backgroundColor: palette.background.default,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <DSCircularProgress sx={{ color: palette.primary.main }} />
      </DSBox>
    );
  }

  const arrowButtonSx = {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    color: palette.common.white,
    backgroundColor: palette.white[10],
    '&:hover': { backgroundColor: palette.white[20] },
  };

  if (totalSlides === 0) {
    return null;
  }

  return (
    <DSBox
      sx={{
        ...(modal
          ? {
              width: '80vw',
              height: '80vh',
              maxWidth: 1000,
              borderRadius: spacing(3),
            }
          : {
              position: 'fixed',
              inset: 0,
              zIndex: zIndex.modal,
            }),
        backgroundColor: palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <DSBox
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          overflow: 'hidden',
          px: { xs: spacing(6), md: spacing(10) },
          py: spacing(2),
        }}
      >
        <img
          src={scaledImages[visibleIndex]}
          alt={`Onboarding slide ${visibleIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: modal ? '100%' : '80vh',
            objectFit: 'contain',
            opacity,
            transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
          }}
        />
      </DSBox>

      {visibleIndex > 0 && (
        <DSIconButton
          onClick={goPrev}
          sx={{ ...arrowButtonSx, left: spacing(2) }}
        >
          <LeftIcon />
        </DSIconButton>
      )}

      <DSIconButton
        onClick={goNext}
        sx={{ ...arrowButtonSx, right: spacing(2) }}
      >
        <RightIcon />
      </DSIconButton>

      <DSBox
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          pb: spacing(3),
          pt: spacing(1),
        }}
      >
        <DSTypography
          sx={{
            color: palette.text.secondary,
            fontSize: 16,
            fontFamily: 'Gilroy-Medium',
          }}
        >
          {visibleIndex + 1}/{totalSlides}
        </DSTypography>

        {!modal && (
          <DSSecondaryButton
            onClick={handleSkip}
            sx={{
              position: 'absolute',
              right: spacing(3),
              color: palette.text.secondary,
              textDecoration: 'underline',
              textTransform: 'none',
              fontSize: 16,
              fontFamily: 'Gilroy-Medium',
              '&:hover': {
                textDecoration: 'underline',
                color: palette.common.white,
                backgroundColor: 'transparent',
              },
            }}
          >
            Skip
          </DSSecondaryButton>
        )}
      </DSBox>
    </DSBox>
  );
};

export default OnboardingWizard;
